import momentTZ from "moment-timezone";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as PackageIcon } from "assets/images/iconsV2/package.svg";
import { ReactComponent as VolumeCubicMeterIcon } from "assets/images/iconsV2/volume-cubic-meter.svg";
import { ReactComponent as WeightIcon } from "assets/images/iconsV2/weight.svg";
import UnitConverter from "components/organisms/UnitConverter/UnitConverter";
import Badge from "components/v2/Badge/Badge";
import { classNames, formatNumber, formatWeight } from "helpers/commonHelpers";

import { DetailsSheetSection } from "../DetailsSheetSection";
import { SHIPMENT_DETAILS_TEST_IDS, shipmentDetailsTestId } from "../testIds";
import type { ShipmentDetailsSheetViewModel } from "../types";
import { StopCell, toAddressPart } from "./packingStopCell";

type PackingListSectionProps = {
  packing: ShipmentDetailsSheetViewModel["packing"];
  isMultistop: boolean;
};

type GoodItem = Record<string, unknown> & {
  handling_unit?: string;
  container_type?: string;
  length?: number;
  width?: number;
  height?: number;
  m_unit?: string;
  weight?: number;
  w_unit?: string;
  units?: number;
  stackable?: boolean;
  crating_required?: boolean;
  crating_required_by_date?: string;
  hazardous?: boolean;
  is_dangerous_good?: boolean;
  dangerous_good_value?: string;
  un_number?: string;
  temperature_control_required?: boolean;
  temperature_control_value?: string;
  temperature_controlled_custom_value?: string;
  temperature_control_custom_value?: string;
  additional_information?: string;
  other_instructions?: string;
  good_customs_information?: Array<Record<string, unknown>>;
  pickup_stop?: Record<string, unknown>;
  delivery_stop?: Record<string, unknown>;
};

const EMPTY = "-";

const formatBoolean = (value: boolean | undefined, intl: ReturnType<typeof useIntl>) => {
  if (value === undefined) return EMPTY;
  return value
    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
    : intl.formatMessage({ id: "general.no", defaultMessage: "No" });
};

const formatCratingBy = (item: GoodItem, intl: ReturnType<typeof useIntl>) => {
  if (!item.crating_required) return EMPTY;
  const date = item.crating_required_by_date;
  if (!date) {
    return intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" });
  }
  const parsed = momentTZ(date);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(date);
};

const isContainerUnit = (handlingUnit?: string) => handlingUnit?.toLowerCase() === "container";
const isTotalWeightUnit = (unit?: string) => Boolean(unit?.toLowerCase().includes("total"));

const OTHER_TEMP_CONTROL = "6";

const formatTempControl = (item: GoodItem) => {
  const custom =
    toAddressPart(item.temperature_controlled_custom_value) ||
    toAddressPart(item.temperature_control_custom_value);
  if (custom) return custom;

  const raw = toAddressPart(item.temperature_control_value);
  if (!raw || raw === OTHER_TEMP_CONTROL) return EMPTY;

  if (raw.toLowerCase().startsWith("other")) {
    const customFromValue = raw.split(/,\s*/).slice(1).join(", ").trim();
    return customFromValue || EMPTY;
  }

  return raw;
};

const getCommodityHsCode = (commodity: Record<string, unknown>) => commodity.hs_code as string | undefined;

const getCommodityCurrency = (commodity: Record<string, unknown>) =>
  (commodity.currency_code as string | undefined) || "USD";

const getCommodityAttachments = (commodity: Record<string, unknown>) => {
  const attachment = commodity.attachment as Record<string, unknown> | Record<string, unknown>[] | undefined;
  if (Array.isArray(attachment)) return attachment;
  if (attachment && (attachment.url || attachment.name)) return [attachment];
  return [];
};


function CommodityCell({ commodities }: { commodities?: Array<Record<string, unknown>> }) {
  if (!commodities?.length) return <span>{EMPTY}</span>;

  return (
    <div className="rfv2-review-packing-list__commodity-cell">
      {commodities.map((commodity, index) => {
        const hsCode = getCommodityHsCode(commodity);
        const { value } = commodity;
        return (
          <div
            key={`${commodity.description}_${index}`}
            className={classNames("rfv2-review-packing-list__commodity-block", {
              "rfv2-review-packing-list__commodity-block--divider": index < commodities.length - 1,
            })}
          >
            {commodity.description && (
              <span className="rfv2-review-packing-list__commodity-description">{String(commodity.description)}</span>
            )}
            {hsCode && (
              <span className="rfv2-review-packing-list__commodity-hs-code">
                <span className="rfv2-review-packing-list__commodity-hs-code-label">
                  <FormattedMessage id="request-form.review.packingList.hsCode" defaultMessage="HS Code:" />{" "}
                </span>
                <span className="rfv2-review-packing-list__commodity-hs-code-value">{String(hsCode)}</span>
              </span>
            )}
            {value !== undefined && value !== null && value !== "" && (
              <div className="rfv2-review-packing-list__commodity-value">
                <span className="rfv2-review-packing-list__commodity-currency">{getCommodityCurrency(commodity)}</span>
                <span className="rfv2-review-packing-list__commodity-amount">{formatNumber(Number(value))}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UnitCell({ item, intl }: { item: GoodItem; intl: ReturnType<typeof useIntl> }) {
  const handlingUnit = item.handling_unit ? String(item.handling_unit) : EMPTY;
  const hasDimensions = Boolean(item.length || item.width || item.height);
  const weightSuffix = isTotalWeightUnit(item.w_unit)
    ? intl.formatMessage({
        id: "request-form.review.packingList.perTotalKg",
        defaultMessage: "/total kg",
      })
    : intl.formatMessage({
        id: "request-form.review.packingList.perUnit",
        defaultMessage: "/unit",
      });

  return (
    <div className="rfv2-review-packing-list__unit-cell">
      <span className="rfv2-review-packing-list__unit-cell-label text-uppercase">{handlingUnit}</span>
      {item.container_type && (
        <span className="rfv2-review-packing-list__unit-cell-line">{String(item.container_type)}</span>
      )}
      {hasDimensions && (
        <span className="rfv2-review-packing-list__unit-cell-line d-flex">
          <UnitConverter content={item.length} value={item.length} unit={item.m_unit} />
          <span className="mx-1">x</span>
          <UnitConverter content={item.width} value={item.width} unit={item.m_unit} />
          <span className="mx-1">x</span>
          <UnitConverter content={item.height} value={item.height} unit={item.m_unit} />
          {item.m_unit}
        </span>
      )}
      {item.weight !== undefined && item.weight !== null && (
        <span className="rfv2-review-packing-list__unit-cell-line d-flex align-items-baseline flex-nowrap">
          <UnitConverter
            unit={item.w_unit}
            value={Number(item.weight).toFixed(2)}
            content={<span>{formatWeight(Number(item.weight).toFixed(2), item.w_unit)}</span>}
          />
          <span className="rfv2-review-packing-list__unit-cell-suffix">{weightSuffix}</span>
        </span>
      )}
    </div>
  );
}

function HandlingCell({ item, intl }: { item: GoodItem; intl: ReturnType<typeof useIntl> }) {
  const isContainer = isContainerUnit(item.handling_unit);
  const dgValue =
    item.is_dangerous_good && item.dangerous_good_value
      ? `${item.dangerous_good_value}${item.un_number ? ` (${item.un_number})` : ""}`
      : EMPTY;

  const handlingLines = [
    !isContainer
      ? {
          label: intl.formatMessage({
            id: "request-form.review.packingList.stackables",
            defaultMessage: "Stackables:",
          }),
          value: formatBoolean(item.stackable, intl),
        }
      : null,
    {
      label: intl.formatMessage({ id: "request-form.review.packingList.hazardous", defaultMessage: "Hazardous:" }),
      value: formatBoolean(item.hazardous, intl),
    },
    {
      label: intl.formatMessage({ id: "request-form.review.packingList.cratingBy", defaultMessage: "Crating by:" }),
      value: formatCratingBy(item, intl),
    },
    {
      label: intl.formatMessage({
        id: "request-form.review.packingList.tempControl",
        defaultMessage: "Temp. control:",
      }),
      value: formatTempControl(item),
    },
    {
      label: intl.formatMessage({ id: "request-form.review.packingList.dg", defaultMessage: "DG:" }),
      value: dgValue,
    },
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  return (
    <div className="rfv2-review-packing-list__handling-cell">
      {handlingLines.map((line) => (
        <p key={line.label} className="rfv2-review-packing-list__handling-line">
          {line.label} <span className="rfv2-review-packing-list__handling-value">{line.value}</span>
        </p>
      ))}
    </div>
  );
}

function UnitAndHandlingCell({ item, intl }: { item: GoodItem; intl: ReturnType<typeof useIntl> }) {
  return (
    <div className="shipment-details-sheet__packing-unit-handling">
      <UnitCell item={item} intl={intl} />
      <HandlingCell item={item} intl={intl} />
    </div>
  );
}


function AdditionalInfoCell({ item }: { item: GoodItem }) {
  const attachments = (item.good_customs_information ?? []).flatMap(getCommodityAttachments);
  const notes = [item.additional_information, item.other_instructions].filter(Boolean) as string[];

  if (!attachments.length && !notes.length) return <span>{EMPTY}</span>;

  return (
    <div className="shipment-details-sheet__packing-add-info">
      {attachments.map((file, index) => {
        const url = file.url as string | undefined;
        const name = String(file.name || url || `file-${index}`);
        if (!url) {
          return (
            <span key={`${name}_${index}`} className="shipment-details-sheet__packing-file">
              {name}
            </span>
          );
        }
        return (
          <a
            key={`${name}_${index}`}
            className="shipment-details-sheet__packing-file"
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            {name}
          </a>
        );
      })}
      {notes.map((note) => (
        <span key={note}>{note}</span>
      ))}
    </div>
  );
}

export function PackingListSection({ packing, isMultistop }: PackingListSectionProps) {
  const intl = useIntl();
  const goodItems = packing.goodItems as GoodItem[];

  if (!goodItems.length) return null;

  const { totals } = packing;

  return (
    <DetailsSheetSection
      testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SECTION_PACKING_LIST}
      title={<FormattedMessage id="general.packingList" defaultMessage="Packing list" />}
    >
      <div className="rfv2-review__section-content rfv2-review-packing-list">
        <div
          className="rfv2-review-packing-list__summary"
          data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PACKING_SUMMARY}
        >
          {packing.goodsEstimated ? (
            <span
              className="shipment-details-sheet__packing-goods-badge"
              data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PACKING_GOODS_ESTIMATED}
            >
              <Badge
                type="status-badge"
                color="brand"
                size="md"
                label={
                  <FormattedMessage
                    id="request-form.packing-list.goodsAreEstimated"
                    defaultMessage="Goods are estimated"
                  />
                }
              />
            </span>
          ) : (
            packing.goodsConfirmationType && (
              <span
                className="shipment-details-sheet__packing-goods-badge"
                data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PACKING_GOODS_FINALIZED}
              >
                <Badge
                  type="status-badge"
                  color="brand"
                  size="md"
                  label={
                    <FormattedMessage
                      id="request-form.packing-list.goodsAreFinalized"
                      defaultMessage="Goods are finalized"
                    />
                  }
                />
              </span>
            )
          )}

          <div className="rfv2-review-packing-list__summary-stats">
            {totals.totalWeight && !Number.isNaN(totals.totalWeight.weight) && (
              <div
                className="rfv2-review-packing-list__summary-stat"
                data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PACKING_TOTAL_WEIGHT}
              >
                <div className="rfv2-review-packing-list__summary-label-group">
                  <WeightIcon />
                  <span>
                    <FormattedMessage id="quote-request-detail.totalWeight" defaultMessage="Total weight" />
                  </span>
                </div>
                <strong>
                  <UnitConverter
                    content={<span>{formatWeight(totals.totalWeight.weight.toFixed(2), totals.totalWeight.unit)}</span>}
                    value={totals.totalWeight.weight.toFixed(2)}
                    unit={totals.totalWeight.unit}
                  />
                </strong>
              </div>
            )}

            <div
              className="rfv2-review-packing-list__summary-stat"
              data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PACKING_TOTAL_CBM}
            >
              <div className="rfv2-review-packing-list__summary-label-group">
                <VolumeCubicMeterIcon />
                <span>
                  <FormattedMessage id="new-request.quote-confirmation.packing-list.CBM" defaultMessage="Total CBM" />
                </span>
              </div>
              <strong>{totals.totalCBM}</strong>
            </div>

            <div
              className="rfv2-review-packing-list__summary-stat"
              data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PACKING_TOTAL_UNITS}
            >
              <div className="rfv2-review-packing-list__summary-label-group">
                <PackageIcon />
                <span>
                  <FormattedMessage id="request-form.review.packingList.totalUnits" defaultMessage="Total units" />
                </span>
              </div>
              <strong>{totals.totalPackages}</strong>
            </div>
          </div>
        </div>

        <div className="shipment-details-sheet__packing-table-wrap">
          <table
            className="shipment-details-sheet__packing-table"
            data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PACKING_TABLE}
          >
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="request-form.review.packingList.no" defaultMessage="No." />
                </th>
                <th>
                  <FormattedMessage id="request-form.review.packingList.qty" defaultMessage="Qty" />
                </th>
                {isMultistop ? (
                  <th>
                    <FormattedMessage
                      id="request-form.review.packingList.unitAndHandling"
                      defaultMessage="Unit & handling"
                    />
                  </th>
                ) : (
                  <>
                    <th>
                      <FormattedMessage id="request-form.review.packingList.unit" defaultMessage="Unit" />
                    </th>
                    <th>
                      <FormattedMessage id="request-form.review.packingList.handling" defaultMessage="Handling" />
                    </th>
                  </>
                )}
                {isMultistop && (
                  <>
                    <th>
                      <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
                    </th>
                    <th>
                      <FormattedMessage id="general.delivery" defaultMessage="Delivery" />
                    </th>
                  </>
                )}
                <th>
                  <FormattedMessage
                    id="request-form.review.packingList.commodityInformation"
                    defaultMessage="Commodity information"
                  />
                </th>
                <th>
                  <FormattedMessage
                    id="request-form.review.packingList.additionalInformation"
                    defaultMessage="Add. info"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {goodItems.map((item, index) => (
                <tr key={`${item.handling_unit}_${index}`}>
                  <td>{index + 1}</td>
                  <td data-testid={shipmentDetailsTestId.packingRow(index, "qty")}>{item.units ?? EMPTY}</td>
                  {isMultistop ? (
                    <td data-testid={shipmentDetailsTestId.packingRow(index, "unit")}>
                      <UnitAndHandlingCell item={item} intl={intl} />
                    </td>
                  ) : (
                    <>
                      <td data-testid={shipmentDetailsTestId.packingRow(index, "unit")}>
                        <UnitCell item={item} intl={intl} />
                      </td>
                      <td data-testid={shipmentDetailsTestId.packingRow(index, "handling")}>
                        <HandlingCell item={item} intl={intl} />
                      </td>
                    </>
                  )}
                  {isMultistop && (
                    <>
                      <td data-testid={shipmentDetailsTestId.packingRow(index, "pickup")}>
                        <StopCell stop={item.pickup_stop} intl={intl} />
                      </td>
                      <td data-testid={shipmentDetailsTestId.packingRow(index, "delivery")}>
                        <StopCell stop={item.delivery_stop} intl={intl} />
                      </td>
                    </>
                  )}
                  <td data-testid={shipmentDetailsTestId.packingRow(index, "commodity")}>
                    <CommodityCell commodities={item.good_customs_information} />
                  </td>
                  <td data-testid={shipmentDetailsTestId.packingRow(index, "description")}>
                    <AdditionalInfoCell item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DetailsSheetSection>
  );
}
